import { z } from 'zod'
import type { JobcardStatus } from '../jobcard-status.js'
import type { LineKind } from './catalog.js'
import type { InspectionTone, InspectionOverviewAngle } from './inspection.js'

/**
 * The status colours the portal uses. Deliberately a local union rather than an
 * import from the client's component props: shared must not depend on the UI
 * layer, and the portal is the only consumer here.
 */
export type PublicTone = 'neutral' | 'info' | 'warning' | 'success' | 'error' | 'violet' | 'teal'

/**
 * The customer-facing view of a jobcard.
 *
 * This is a DELIBERATELY SEPARATE projection, not a subset of `JobcardDetail`.
 * It is built by an explicit mapper that names every field it copies, so a
 * field added to the internal jobcard later cannot silently appear on a
 * customer's screen. Never build it by spreading an internal object.
 *
 * It carries NO cost, NO margin and NO staff notes. A test serialises a
 * populated view and fails if a cost figure appears in it.
 */

/** 22 characters of base64url — 128 bits of randomness. */
export const publicTokenSchema = z.string().min(16).max(64)

export type PaymentState = 'not_due' | 'awaiting_payment' | 'paid'

/**
 * What the customer is told, in place of the internal status name.
 *
 * Exhaustive over `JobcardStatus`: adding a status without wording for it is a
 * compile error rather than a blank line on someone's phone.
 */
export const PUBLIC_STATUS_LABELS: Record<JobcardStatus, string> = {
  draft: 'Received',
  awaiting_approval: 'Quotation sent — awaiting your approval',
  in_progress: 'In progress',
  completed: 'Ready — invoice on its way',
  invoiced: 'Ready — awaiting payment',
  paid: 'Paid — thank you',
  cancelled: 'Cancelled',
}

/** A short line under the status, so the customer knows what happens next. */
export const PUBLIC_STATUS_HINTS: Record<JobcardStatus, string> = {
  draft: 'We have your car and are looking at it.',
  awaiting_approval: 'Please review the quotation and let us know if we should go ahead.',
  in_progress: 'Work is under way. We will let you know when it is ready.',
  completed: 'Your car is ready. We will send the invoice shortly.',
  invoiced: 'The work is finished. Payment is due.',
  paid: 'Everything is settled. Thank you.',
  cancelled: 'This job was cancelled. Contact us if that is unexpected.',
}

/**
 * The car, and only the car.
 *
 * PUBLIC_STATUS_LABELS answers two questions in one line — "Ready — awaiting
 * payment" — and a customer is usually asking only one of them. Split, each
 * gets a line of its own and both stay short enough to read at a glance on a
 * phone. The combined label is kept: the WhatsApp status message is one line
 * by nature and wants both halves in it.
 */
export const PUBLIC_WORK_LABELS: Record<JobcardStatus, string> = {
  draft: 'Received',
  awaiting_approval: 'Quotation sent',
  in_progress: 'In progress',
  completed: 'Ready',
  invoiced: 'Ready',
  paid: 'Complete',
  cancelled: 'Cancelled',
}

/**
 * The money, and only the money.
 *
 * Reads the DEPOSIT as well as the state, because "nothing to pay yet" is
 * plainly wrong to someone who handed over 500 on the forecourt, and "payment
 * due" hides that most of it is already settled.
 */
export function publicPaymentLabel(
  state: PaymentState,
  amountPaidMinor: number,
  status: JobcardStatus,
): string {
  if (status === 'cancelled') return 'Nothing to pay'
  if (state === 'paid') return 'Paid in full'
  if (state === 'awaiting_payment') return amountPaidMinor > 0 ? 'Balance due' : 'Payment due'
  return amountPaidMinor > 0 ? 'Deposit received' : 'Nothing to pay yet'
}

export const PUBLIC_STATUS_TONES: Record<JobcardStatus, PublicTone> = {
  draft: 'neutral',
  awaiting_approval: 'warning',
  in_progress: 'info',
  completed: 'teal',
  invoiced: 'violet',
  paid: 'success',
  cancelled: 'error',
}

/** Payment state follows the jobcard's own status; a customer cannot set it. */
export function paymentStateFor(status: JobcardStatus): PaymentState {
  if (status === 'paid') return 'paid'
  if (status === 'invoiced') return 'awaiting_payment'
  return 'not_due'
}

export interface PublicLine {
  kind: LineKind
  name: string
  partNumber: string | null
  quantityMilli: number
  /** What the customer is charged per unit. Our cost is never included. */
  rateMinor: number
  lineTotalMinor: number
}

export interface PublicDocument {
  id: string
  type: 'quotation' | 'invoice'
  number: string
  issuedAt: string
  totalMinor: number
}

/**
 * A customer-facing inspection item. Only items with a non-empty status are
 * included in the public view. NEVER includes photoPath or any filesystem path.
 * This is a deliberate projection of the internal InspectionItemState.
 */
export interface PublicInspectionItem {
  id: string
  label: string
  scale: 'condition' | 'severity' | 'yesno'
  status: string
  statusLabel: string
  tone: InspectionTone
  comment: string | null
  hasPhoto: boolean
}

/**
 * A customer-facing inspection section. Only sections with at least one rated
 * item or a non-empty note are included in the public view.
 */
export interface PublicInspectionSection {
  id: string
  label: string
  note: string | null
  items: PublicInspectionItem[]
}

/**
 * A customer-facing inspection report. Includes only angles that actually have
 * photos, in the fixed INSPECTION_OVERVIEW_ANGLES order. Null when the jobcard
 * has no inspection or nothing in it is filled in yet.
 *
 * This is a deliberately separate projection, built by an explicit mapper that
 * names every field it copies, so no internal field can silently appear on a
 * customer's screen.
 */
export interface PublicInspection {
  performedAt: string | null
  performedBy: string | null
  overallNotes: string | null
  summary: {
    rated: number // items with a status (including neutral)
    good: number // rated items whose tone is 'success'
    attention: number // rated items whose tone is 'warning' or 'error'
  }
  sections: PublicInspectionSection[]
  overview: Array<{ angle: InspectionOverviewAngle; label: string }>
}

export interface PublicJobcardView {
  jobcard: {
    number: string
    /** Customer wording, not the internal status name. Both halves in one line. */
    statusLabel: string
    /** What is happening to the CAR: "In progress", "Ready". */
    workLabel: string
    /** What is happening to the MONEY: "Balance due", "Paid in full". */
    paymentLabel: string
    statusHint: string
    statusTone: PublicTone
    /** What the customer told us. Staff notes are never included. */
    complaint: string | null
    updatedAt: string
    /** True only in 'awaiting_approval' — the portal offers Approve/Decline right now. */
    awaitingApproval: boolean
  }
  car: {
    brand: string
    model: string
    year: number | null
    plate: string
    color: string | null
  }
  inspection: PublicInspection | null
  lines: PublicLine[]
  totals: {
    subtotalMinor: number
    discountMinor: number
    vatMinor: number
    vatLabel: string
    vatRateBp: number
    vatEnabled: boolean
    totalMinor: number
    /**
     * Deposit already received. Zero when there is none.
     *
     * The customer knows they handed money over; a page that shows only the
     * full total reads as though it was never recorded, which is exactly the
     * moment someone rings the workshop.
     */
    amountPaidMinor: number
    /** What is left to pay. Equals the total when there is no deposit. */
    amountDueMinor: number
    currencySymbol: string
    currencyDecimals: number
  }
  documents: PublicDocument[]
  payment: {
    state: PaymentState
    /** Only present when the garage set one AND payment is awaited. */
    paymentUrl: string | null
    bankDetails: string | null
  }
  garage: {
    name: string
    phone: string | null
    /** Digits only, for wa.me. Null when the garage has no number set. */
    whatsappDigits: string | null
    address: string
    hasLogo: boolean
  }
}

/** Absolute link handed to a customer. Requires the configured public base. */
export function publicJobcardUrl(baseUrl: string, token: string): string {
  return `${baseUrl.replace(/\/+$/, '')}/p/${token}`
}

/**
 * The customer's response to a quotation, sent from the portal.
 * 'decline' rather than 'reject': friendlier wording for the person reading it,
 * translated to the jobcard state machine's 'reject' action on the server.
 */
export const publicQuotationDecisionSchema = z.object({
  decision: z.enum(['approve', 'decline']),
})
export type PublicQuotationDecisionRequest = z.infer<typeof publicQuotationDecisionSchema>
